import * as monaco from 'monaco-editor';
import {
  DidChangeTextDocumentNotification,
  DidCloseTextDocumentNotification,
  DidOpenTextDocumentNotification,
  ProtocolConnection,
  ServerCapabilities,
  TextDocumentSyncKind,
} from 'vscode-languageserver-protocol/browser';

import { assertUnreachableOrReturnDefault } from '../utils/assertUnreachable';

export function textDocumentSync(
  languageId: string,
  connection: ProtocolConnection,
  textDocumentSyncOptions: ServerCapabilities<unknown>['textDocumentSync'],
) {
  if (textDocumentSyncOptions === undefined) return;

  const {
    openClose: sendOpenCloseNotifications = false,
    change = TextDocumentSyncKind.None,

    // TODO: implement these?
    // willSave: sendWillSaveNotifications = false,
    // willSaveWaitUntil: sendWillSaveWaitUntilRequests = false,
    // save: sendSaveNotifications = false,
  } =
    typeof textDocumentSyncOptions === 'number'
      ? { change: textDocumentSyncOptions }
      : textDocumentSyncOptions;

  const modelCleanup = new Map<monaco.editor.ITextModel, () => void>();
  const onModelRemoved = (model: monaco.editor.ITextModel) => {
    monaco.editor.setModelMarkers(model, languageId, []);
    const cleanup = modelCleanup.get(model);
    if (cleanup) {
      cleanup();
      modelCleanup.delete(model);
    }
  };

  const onModelAdd = (model: monaco.editor.ITextModel) => {
    if (model.getLanguageId() !== languageId) {
      return;
    }

    if (sendOpenCloseNotifications) {
      connection.sendNotification(DidOpenTextDocumentNotification.type, {
        textDocument: {
          languageId,
          text: model.getValue(),
          uri: model.uri.toString(true),
          version: model.getVersionId(),
        },
      });
    }

    const disposable = model.onDidChangeContent(() => {
      switch (change) {
        case TextDocumentSyncKind.None:
          break;
        case TextDocumentSyncKind.Incremental:
          // TODO: implement incremental
          if (process.env.NODE_ENV === 'production') {
            break;
          } else {
            throw new Error('Incremental updates are not supported');
          }
        case TextDocumentSyncKind.Full:
          connection.sendNotification(DidChangeTextDocumentNotification.type, {
            contentChanges: [{ text: model.getValue() }],
            textDocument: {
              uri: model.uri.toString(true),
              version: model.getVersionId(),
            },
          });
          break;
        default:
          assertUnreachableOrReturnDefault(change, null);
      }
    });
    modelCleanup.set(model, () => {
      disposable.dispose();
      if (sendOpenCloseNotifications) {
        connection.sendNotification(DidCloseTextDocumentNotification.type, {
          textDocument: { uri: model.uri.toString(true) },
        });
      }
    });
  };

  monaco.editor.onDidCreateModel(onModelAdd);
  monaco.editor.onWillDisposeModel(onModelRemoved);
  monaco.editor.onDidChangeModelLanguage((event) => {
    onModelRemoved(event.model);
    onModelAdd(event.model);
  });

  return onModelAdd;
}
